import type { ContratProADTO } from '@/features/api/extranet/dto/Demande/PROA'
import { TypesContextesAutorisesEnum, type TypeContextCourant } from '@/features/auth/context-app'
import { PropertyStatus, type PropertyStatusConfiguration } from '@/features/domaine/property-status'
import { isNotNullUndefinedOrEmptyString } from '@/features/shared/tools'
import { idccAutoriseTT } from '../shared'
import type { ContextReglesProA } from './types'

function estTT(model: ContratProADTO) {
  return idccAutoriseTT.includes(model.employeur?.etablissement?.idcc?.code ?? '')
}

function selonTypeContextCourant(
  typeContext: TypeContextCourant,
  conditionVrai: PropertyStatus,
  conditionFaux: PropertyStatus,
) {
  return function (_value: any, _parent: any, _modele: ContratProADTO, context: ContextReglesProA) {
    if (typeContext === TypesContextesAutorisesEnum.Adherent) {
      return context.typeContext === TypesContextesAutorisesEnum.Adherent ? conditionVrai : conditionFaux
    } else {
      return context.typeContext === TypesContextesAutorisesEnum.Prestataire ? conditionVrai : conditionFaux
    }
  }
}

const optionnelPrestataire = selonTypeContextCourant(
  TypesContextesAutorisesEnum.Adherent,
  PropertyStatus.Required,
  PropertyStatus.Optional,
)
/*
const requiredOrNotPresentPrestataire = selonTypeContextCourant(
  TypesContextesAutorisesEnum.Adherent,
  PropertyStatus.Required,
  PropertyStatus.NotPresent,
)
*/

export const propertyStatusConfiguration: PropertyStatusConfiguration<ContratProADTO, ContextReglesProA> = {
  lieuSignature: optionnelPrestataire,
  dateSignature: optionnelPrestataire,
  attestationPiecesJustificatives: optionnelPrestataire,
  employeur: {
    contact: {
      email: PropertyStatus.Required,
    },
  },
  salarie: {
    nomNaissance: PropertyStatus.Required,
    prenom: PropertyStatus.Required,
    sexe: PropertyStatus.Required,
    dateNaissance: PropertyStatus.Required,
    nirTemporaire: optionnelPrestataire,
    nir: optionnelPrestataire,
    beneficieReconnaissanceTravailleurHandicape: optionnelPrestataire,
    adresse: {
      voie: optionnelPrestataire,
      codePostal: optionnelPrestataire,
      commune: optionnelPrestataire,
    },
    email: optionnelPrestataire,

    diplomeOuTitreLePlusEleveObtenu: optionnelPrestataire,
    inscritPoleEmploi: optionnelPrestataire,
    situation: optionnelPrestataire,

    csp: optionnelPrestataire,
    statutRefugie: PropertyStatus.NotPresent,
    publicSpecifique: (_value, _parent, model, _context) => {
      if (estTT(model)) {
        return PropertyStatus.Optional
      }
      return PropertyStatus.NotPresent
    },
  },
  tutorat: {
    tuteurEntrepriseEmployeur: {
      nom: optionnelPrestataire,
      prenom: optionnelPrestataire,
      dateNaissance: optionnelPrestataire,
      souhaiteAideFonctionTutorale: (_value, _parent, model, context) => {
        if (estTT(model)) {
          return context.typeContext === TypesContextesAutorisesEnum.Prestataire
            ? PropertyStatus.NotPresent
            : PropertyStatus.Required
        }
        return PropertyStatus.NotPresent
      },
    },
    tuteurEntrepriseUtilisatrice: {
      nom: (_value, parent, _model, context) => {
        if (context.typeContext === TypesContextesAutorisesEnum.Prestataire) return PropertyStatus.Optional

        if (isNotNullUndefinedOrEmptyString(parent.nom) || isNotNullUndefinedOrEmptyString(parent.prenom))
          return PropertyStatus.Required

        return PropertyStatus.Optional
      },
      prenom: (_value, parent, _model, context) => {
        if (context.typeContext === TypesContextesAutorisesEnum.Prestataire) return PropertyStatus.Optional

        if (isNotNullUndefinedOrEmptyString(parent.nom) || isNotNullUndefinedOrEmptyString(parent.prenom))
          return PropertyStatus.Required

        return PropertyStatus.Optional
      },
      dateNaissance: (_value, parent, _model, context) => {
        if (context.typeContext === TypesContextesAutorisesEnum.Prestataire) return PropertyStatus.Optional

        if (isNotNullUndefinedOrEmptyString(parent.nom) || isNotNullUndefinedOrEmptyString(parent.prenom))
          return PropertyStatus.Required

        return PropertyStatus.Optional
      },
    },
    attestationTuteurCriteresEligibilite: optionnelPrestataire,
    attestationTuteurTroisStagiaires: optionnelPrestataire,
  },
  contrat: {
    emploiOccupe: optionnelPrestataire,
    emploiCcn: optionnelPrestataire,
    niveauQualificationCcn: optionnelPrestataire,
    dateDebutExecutionContrat: optionnelPrestataire,

    proaVise: optionnelPrestataire,
    emploiVise: optionnelPrestataire,

    emploiCcnVise: optionnelPrestataire,
    niveauQualificationCcnVise: optionnelPrestataire,

    dateDebutExecutionProA: optionnelPrestataire,
    dateFinExecutionProA: optionnelPrestataire,

    dureeHebdomadaire: {
      heure: optionnelPrestataire,
      minute: optionnelPrestataire,
    },
  },
  formation: {
    intitule: PropertyStatus.Required,
    typeQualification: PropertyStatus.Required,
    rncp: {
      code: (...[, , model]) =>
        model.formation?.typeQualification && model.formation.typeQualification !== 'Clea'
          ? PropertyStatus.Required
          : PropertyStatus.NotPresent,
      /*
      code: (_value, _parent, model) => {
        if (!model.sousDispositif) return PropertyStatus.NotPresent
        if (model.formation?.typeQualification === 'Rncp') {
          return PropertyStatus.Required
        } else if (model.formation?.typeQualification === 'Cqp') {
          return model.formation?.typeReferentielFormation === 'Rncp'
            ? PropertyStatus.Required
            : PropertyStatus.NotPresent
        }
        return PropertyStatus.NotPresent
      },
      */
    },
    repertoireSpecifique: {
      code: (...[, , model]) =>
        model.formation?.typeQualification && model.formation.typeQualification === 'Clea'
          ? PropertyStatus.Required
          : PropertyStatus.NotPresent,
    },
    nsf: {
      code: (...[, , model]) => {
        if (!model.formation?.typeQualification) return PropertyStatus.NotPresent
        if (model.formation.typeQualification === 'Clea') {
          return model.formation.repertoireSpecifique?.code ? PropertyStatus.Required : PropertyStatus.NotPresent
        } else {
          return model.formation.rncp?.code ? PropertyStatus.Required : PropertyStatus.NotPresent
        }
      },
    },
    certificationVisee: PropertyStatus.Required,
    //diplomeOuTitreVise: PropertyStatus.Required,
    dureeTotale: PropertyStatus.Required,
    dureeEnseignementsGenerauxEtTechnologique: PropertyStatus.Required,
    modules: {
      etablissementPrestataire: {
        siret: {
          valeur: PropertyStatus.Required,
          siren: PropertyStatus.Required,
          nic: PropertyStatus.Required,
        },
      },
      modalitePedagogique: PropertyStatus.Required,
      dureeFoad: (_value, _index, parent, _model, _context) => {
        return parent.modalitePedagogique === 'Mixte' ? PropertyStatus.Required : PropertyStatus.Optional
      },
      dateDebut: PropertyStatus.Required,
      dateFin: PropertyStatus.Required,
      duree: PropertyStatus.Required,

      avecSubrogation: (_value, _index, parent, _model, _context) => {
        return parent.typeModule === 'Interne' ? PropertyStatus.NotPresent : PropertyStatus.Required
      },
      attestationMoyenRealisation: (_value, _index, parent, _model, _context) => {
        return parent.typeModule === 'Interne' ? PropertyStatus.Required : PropertyStatus.NotPresent
      },

      fraisPedagogiques: {
        // montantCoutPedagogiques: PropertyStatus.Required,
        montant: (_value, _index, parent, _model, _context) => {
          return parent.nature! === 'Pedagogique' ? PropertyStatus.Required : PropertyStatus.Optional
        },
      },
      fraisAnnexesSalarie: {
        montant: (_value, _index, parent, _model, _context) => {
          if (typeof parent.quantite !== 'number') return PropertyStatus.Optional
          else return PropertyStatus.Required
        },
        quantite: (_value, _index, parent, _model, _context) => {
          if (parent.baseCalcul !== 'Quantite') return PropertyStatus.NotPresent
          if (typeof parent.montant !== 'number') return PropertyStatus.Optional
          return PropertyStatus.Required
        },
      },
    },
  },

  consentementTraitementDonnees: PropertyStatus.Required,
  financement: {
    $model: (_value, _parent, model, context) => {
      if (!estTT(model))
        return PropertyStatus.NotPresent
      if (context.typeContext === TypesContextesAutorisesEnum.Prestataire)
        return PropertyStatus.NotPresent
    },
    attestationResteChargeContribution: PropertyStatus.Required,
    modePriseEnChargeReste: (_value, parent) => {
      if (parent.attestationResteChargeContribution === true)
        return PropertyStatus.Required
      return PropertyStatus.NotPresent
    },
  },
} as const
